import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { HdGateInfoContext } from '../hd-gate-info-context.model';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../hd-content-language.service';
import { HdGateInfoService } from '../../hd-gate-info.service';
import { CommunityContentAccessService } from '../../../../community-content/community-content-access.service';
import { HdLineCompanionPanelService } from '../../../../community-content/line-companion/hd-line-companion-panel.service';
import { HdIChingAccessService } from '../../../../iching/hd-iching-access.service';
import { HdIChingPanelService } from '../../../../iching/panel/hd-iching-panel.service';
import { IChingLinePosition } from '../../../../iching/core/iching.models';
import { withHdFearIconDetail } from '../line/hd-gate-line.view-model';
import {
  HD_BODYGRAPH_ENHANCED_DEEP_COLOR_CONFIG,
  HD_BODYGRAPH_ENHANCED_LIGHT_COLOR_CONFIG,
  HdBodygraphColorConfig,
  isHdBodygraphDarkCenterColor,
} from '../../hd-bodygraph-color-config';
import { toVeryDarkVariant, toVeryLightVariant } from '@shared/utils/color.utils';
import {
  HD_BODYGRAPH_CIRCUIT_THEME_ICONS,
  resolveHdGateFooterCircuits,
} from '../../hd-bodygraph-circuit-metadata';
import { HdBodygraphIconKey, HdGateIconLayout, getHdGateIconLayout } from '../../hd-bodygraph-icons';
import { HdGateAttributeIconComponent } from '../../gate-attributes/hd-gate-attribute-icon.component';
import { getHdPlanetDisplayConfigByPlanetId } from '../../planet-grid/hd-planet-display-config';

interface HdCheatSheetFooterCircuitViewModel {
  readonly key: string;
  readonly name: string;
  readonly color: string;
  readonly themeIcon: string | undefined;
  readonly themeLabel: string | undefined;
}

interface HdCheatSheetIconViewModel {
  readonly key: HdBodygraphIconKey;
  readonly label: string;
}

type HdCheatSheetIconRowViewModel = readonly HdCheatSheetIconViewModel[];

/**
 * Visuele staat van de cheat-sheet kaart:
 * - `defined`: standaard — center-kleur als achtergrond + border, footer wit (zoals A3-poster).
 * - `undefined`: lichtgrijze achtergrond, border in center-kleur, diagonale hatch in center-kleur
 *   (analoog aan undefined planet-grid blokjes).
 * - `unactivated`: lichtgrijze achtergrond, geen hatch, donkergrijze border (poort heeft geen activatie).
 *
 * Default = `'defined'` zodat bestaand gebruik (cheat-sheet pagina e.d.) ongewijzigd blijft.
 */
export type HdGateInfoCheatSheetVisualState = 'defined' | 'undefined' | 'unactivated';

const HD_CHEAT_SHEET_INACTIVE_BACKGROUND = 'rgb(243, 243, 243)';
const HD_CHEAT_SHEET_UNACTIVATED_BORDER = 'rgb(120, 120, 120)';

/**
 * Compacte cheat-sheet-kaart voor één poort, gemodelleerd naar de A3-poster
 * (`docs/human-design/A3 Human Design Gates Cheat Sheet Minis NL.pdf`).
 *
 * Lay-out:
 * - Header: iching-glyph + gate-nummer + keynote 1/2 (links), center-label + property-iconen (rechts).
 * - Body: korte iching-omschrijving, extra notes, en rechtsonder physical/dna notes.
 * - Footer: circuit-blokje met circuit-naam en theme-icoon (links), kanaal + primary partner (rechts).
 *
 * Achtergrondkleur = `enhanced-light` center-kleur. Voor integratie-poorten toont
 * de footer een duo-color blokje (helft integratie, helft het andere circuit van de poort).
 *
 * Iconen rechtsboven worden gestuurd door `HD_GATE_ICONS` — geen runtime-logica.
 */
@Component({
  selector: 'hd-gate-info-cheat-sheet',
  templateUrl: './hd-gate-info-cheat-sheet.component.html',
  styleUrl: './hd-gate-info-cheat-sheet.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdGateAttributeIconComponent],
  host: {
    '[class.is-clickable]': 'isClickable()',
    '(click)': 'onCardClick()',
  },
})
export class HdGateInfoCheatSheetComponent {
  private readonly gateInfoService = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly context = input.required<HdGateInfoContext>();

  /**
   * Verbergt het poortnummer + iching-glyph in de header. De ruimte blijft
   * behouden (visibility:hidden) zodat het cheat-sheet-grid niet schuift.
   * Gebruikt door de `memory-poort`-spelmodus en door de cheat-sheet-pagina
   * (toggle "poortcijfers").
   */
  readonly hideGateNumber = input(false);

  /**
   * Verbergt keynote 1 én 2 in de header. Ruimte behouden via visibility:hidden.
   * Gebruikt door `memory-keynote` en door de cheat-sheet-pagina (toggle "keynotes").
   */
  readonly hideKeynotes = input(false);

  /**
   * Verbergt de visuele kleur-info (achtergrondkleur, randkleur, center-label,
   * property-iconen rechtsboven, het circuit-kleurblokje in de footer én de
   * fysiologie-/DNA-noot, die het center verraadt). Card-vorm en lay-out
   * blijven behouden. Gebruikt door de cheat-sheet-pagina (toggle "kleuren +
   * center") en de inkleur-spelmodus.
   */
  readonly hideColors = input(false);

  /**
   * Verbergt het circuit-blok in de witte onderbalk (kleurblokje, circuit-naam en
   * theme-icoon). De onderbalk zelf en het rechterstuk blijven zichtbaar; de ruimte
   * blijft behouden via visibility:hidden zodat de kaarthoogte ongewijzigd blijft.
   * Gebruikt door de cheat-sheet-pagina (toggle "circuit").
   */
  readonly hideFooterCircuit = input(false);

  /**
   * Verbergt de kanaal-key + primary-partner tekst rechts in de onderbalk. De
   * onderbalk en het circuit-blok blijven zichtbaar; de ruimte blijft behouden via
   * visibility:hidden. Gebruikt door `memory-poort` en de cheat-sheet-pagina
   * (toggle "kanaal + pp").
   */
  readonly hideFooterChannel = input(false);

  /**
   * Verbergt het volledige body-blok: korte iching-omschrijving, extra notes en
   * de physical/dna-noot rechtsonder. Ruimte behouden via visibility:hidden zodat
   * het cheat-sheet-grid niet schuift. Gebruikt door de cheat-sheet-pagina
   * (toggle "body content").
   */
  readonly hideBody = input(false);

  /**
   * Visuele staat van de kaart (zie {@link HdGateInfoCheatSheetVisualState}).
   * Default `'defined'` houdt bestaand gebruik (cheat-sheet pagina) ongewijzigd.
   * Wanneer `hideColors` actief is wordt deze input genegeerd (de kaart krijgt
   * de neutrale "kleuren-uit" styling die al bestond).
   */
  readonly visualState = input<HdGateInfoCheatSheetVisualState>('defined');

  /**
   * Tijdelijke per-card override die alle hide-flags negeert. De grid op de
   * cheat-sheet-pagina zet dit per kaart aan/uit op basis van klikken; de card
   * zelf is volledig presentational.
   */
  readonly revealOverride = input(false);

  /**
   * Globale body-weergave gestuurd vanuit de controls-bar (toggle "lijn-keynotes").
   * Wijzigingen aan deze input clearen elke per-card lokale override zodat de
   * globale knop voor alle kaarten weer eenduidig werkt.
   */
  readonly bodyView = input<'default' | 'lines'>('default');

  /**
   * Optionele color-config override. Wanneer ingevuld worden de center-kleuren
   * uit deze config gebruikt voor de achtergrond van de kaart (en een afgeleide
   * donkere variant voor de border) in plaats van de standaard Enhanced Light /
   * Enhanced Deep paren. Laat `null` voor het oorspronkelijke gedrag.
   */
  readonly colorConfig = input<HdBodygraphColorConfig | null>(null);

  /** Wordt geëmit zodra de gebruiker op de kaart klikt (parent beslist over override). */
  readonly cardClicked = output<number>();

  protected readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);
  protected readonly gateInfo = computed(() => this.gateInfoService.gateInfoIn(this.context().gate, this.lang()));

  private readonly definedBackgroundColor = computed(() => {
    const override = this.colorConfig();
    const center = this.gateInfo().center;
    if (override) {
      // Donkere centerkleur (Whale): de kaarttekst is donker, dus een
      // lichte tint als achtergrond; het kader houdt de donkere kleur.
      const centerColor = override.centerDefined[center];
      return isHdBodygraphDarkCenterColor(centerColor) ? toVeryLightVariant(centerColor) : centerColor;
    }
    return HD_BODYGRAPH_ENHANCED_LIGHT_COLOR_CONFIG.centerDefined[center];
  });

  /**
   * Border-kleur in `defined`-staat = de "diepere" centerkleur uit het
   * enhanced-deep preset zodat het kader visueel duidelijk rond de hele kaart
   * staat (incl. de witte onderbalk) zonder met de lichte achtergrond te
   * versmelten. Wanneer er een color-config override actief is wordt een
   * donkere variant afgeleid van de gekozen center-kleur.
   */
  private readonly definedBorderColor = computed(() => {
    const override = this.colorConfig();
    const center = this.gateInfo().center;
    if (override) {
      return toVeryDarkVariant(override.centerDefined[center]);
    }
    return HD_BODYGRAPH_ENHANCED_DEEP_COLOR_CONFIG.centerDefined[center];
  });

  protected readonly backgroundColor = computed(() => {
    const state = this.visualState();
    if (state === 'undefined' || state === 'unactivated') {
      return HD_CHEAT_SHEET_INACTIVE_BACKGROUND;
    }
    return this.definedBackgroundColor();
  });

  protected readonly borderColor = computed(() => {
    const state = this.visualState();
    if (state === 'unactivated') {
      return HD_CHEAT_SHEET_UNACTIVATED_BORDER;
    }
    if (state === 'undefined') {
      return this.definedBorderColor();
    }
    return this.definedBorderColor();
  });

  /**
   * Hatch-kleur enkel in `undefined`-staat (de center-kleur, zoals undefined
   * planet-grid blokjes). `null` in andere staten zodat geen overlay rendert.
   */
  protected readonly hatchColor = computed<string | null>(() => {
    if (this.visualState() !== 'undefined') return null;
    return this.definedBorderColor();
  });

  protected readonly isUnactivatedState = computed(() => this.visualState() === 'unactivated');
  protected readonly isUndefinedState = computed(() => this.visualState() === 'undefined');

  protected readonly centerLabel = computed(
    () => this.gateInfoService.centerLabelsIn(this.lang())[this.gateInfo().center],
  );

  protected readonly iconLayout = computed<HdGateIconLayout>(
    () => getHdGateIconLayout(this.gateInfo().gate),
  );

  /**
   * Lay-out van iconen met (vertaalde) tooltip per icoon-key; klikbaarheid en
   * panelen regelt {@link HdGateAttributeIconComponent}.
   */
  protected readonly iconLayoutViewModel = computed<readonly HdCheatSheetIconRowViewModel[]>(() => {
    const labels = this.gateInfoService.iconLabelsIn(this.lang());
    const fearDetail = this.gateInfoService.fearIconDetailIn(this.gateInfo().gate, this.lang());
    return this.iconLayout().map(row =>
      row.map(key => ({ key, label: withHdFearIconDetail(key, labels[key], fearDetail) })),
    );
  });

  protected readonly physicalAndDnaNote = computed(() => {
    const { physicalNotes, dnaNote } = this.gateInfo().translations;
    return [...physicalNotes, dnaNote].filter(part => !!part).join(', ');
  });

  protected readonly footerCircuits = computed<HdCheatSheetFooterCircuitViewModel[]>(() => {
    const circuitNames = this.gateInfoService.circuitNamesIn(this.lang());
    const themeLabels = this.gateInfoService.circuitThemeLabelsIn(this.lang());
    const circuits = resolveHdGateFooterCircuits(this.gateInfo().circuits);
    const useShortName = circuits.length > 1;
    return circuits.map(circuit => {
      const labels = circuitNames[circuit.key];
      const fullName = labels?.name ?? circuit.key;
      const shortName = labels?.shortName ?? fullName;
      const preferShort = useShortName || circuit.key === 'collectief-abstract';
      return {
        key: circuit.key,
        name: preferShort ? shortName : fullName,
        color: circuit.color,
        themeIcon: circuit.theme ? HD_BODYGRAPH_CIRCUIT_THEME_ICONS[circuit.theme] : undefined,
        themeLabel: circuit.theme ? themeLabels[circuit.theme] : undefined,
      };
    });
  });

  protected readonly footerRight = computed(() => {
    const info = this.gateInfo();
    const channelText = formatHdCheatSheetChannelKeys(info.channelKeys, info.gate);
    const partner = info.metadata.primaryPartnerGate;
    return partner ? `${channelText}, pp ${partner}` : channelText;
  });

  /** True wanneer minstens één hide-flag actief is (en reveal niet aan staat). */
  protected readonly anyFlagActive = computed(
    () => this.hideGateNumber()
      || this.hideKeynotes()
      || this.hideColors()
      || this.hideFooterCircuit()
      || this.hideFooterChannel()
      || this.hideBody(),
  );

  protected readonly isClickable = computed(() => this.anyFlagActive());

  /**
   * Per-card override op de globale `bodyView`-input. `null` betekent geen
   * override (de globale waarde wordt gevolgd). Wanneer de gebruiker op de
   * lokale toggle-knop klikt wordt hier de tegengestelde waarde van de huidige
   * effectieve weergave gezet. Een wijziging aan de globale input clearet deze
   * override (zie effect in de constructor).
   */
  private readonly localBodyViewOverride = signal<'default' | 'lines' | null>(null);

  /**
   * Welke variant van het body-blok zichtbaar is. `default` toont de
   * iching-omschrijving + extra notes + physical/DNA noot. `lines` vervangt
   * dat blok door de 6 lijn-keynotes in aflopende volgorde (lijn 6 bovenaan,
   * lijn 1 onderaan — analoog aan de iching-symboliek).
   */
  protected readonly effectiveBodyView = computed<'default' | 'lines'>(
    () => this.localBodyViewOverride() ?? this.bodyView(),
  );

  /**
   * Wordt `true` na de eerste body-view wissel (lokaal of via globale input)
   * en stuurt de slide-animatie van het body-content blok. Initieel `false`
   * zodat de 64 kaarten niet allemaal animeren bij eerste render.
   */
  protected readonly bodyViewAnimated = signal(false);

  constructor() {
    let isFirstBodyViewRun = true;
    effect(() => {
      this.bodyView();
      if (isFirstBodyViewRun) {
        isFirstBodyViewRun = false;
        return;
      }
      this.localBodyViewOverride.set(null);
      this.bodyViewAnimated.set(true);
    });
  }

  /**
   * Lijn-keynotes in aflopende volgorde (lijn 6 → lijn 1). Volgt de iching
   * convention waarbij lijn 1 onderaan het hexagram staat.
   *
   * Wanneer er chart-context is (`context().activations` is gevuld) worden per
   * lijn ook de activerende planeten meegegeven (glyph + side) zodat de
   * lijn-rij visueel kan signaleren welke lijnen in de huidige chart effectief
   * geactiveerd zijn (zie scss: `is-activated`-modifier + glyph-cel).
   */
  protected readonly descendingLineKeynotes = computed<
    readonly { lineNumber: number; keynote: string; activatingPlanets: readonly { glyph: string; side: 'personality' | 'design' }[] }[]
  >(() => {
    const keynotes = this.gateInfo().translations.lineKeynotes;
    const activations = this.context().activations ?? [];
    const rows: { lineNumber: number; keynote: string; activatingPlanets: { glyph: string; side: 'personality' | 'design' }[] }[] = [];
    for (let lineNumber = 6; lineNumber >= 1; lineNumber--) {
      const activatingPlanets = activations
        .filter(activation =>
          activation.line === lineNumber
          && (activation.side === 'personality' || activation.side === 'design'),
        )
        .map(activation => ({
          glyph: getHdPlanetDisplayConfigByPlanetId(activation.planetId)?.astronomiconChar ?? '',
          side: activation.side as 'personality' | 'design',
        }));
      rows.push({ lineNumber, keynote: keynotes[lineNumber - 1] ?? '', activatingPlanets });
    }
    return rows;
  });

  protected readonly tooltipBodyToggle = 'Wissel weergave (info ↔ lijn-keynotes)';
  protected readonly ariaLabelBodyToggle = 'Wissel body-weergave tussen standaard info en lijn-keynotes';

  protected toggleBodyView(event: MouseEvent): void {
    event.stopPropagation();
    const next = this.effectiveBodyView() === 'default' ? 'lines' : 'default';
    this.localBodyViewOverride.set(next);
    this.bodyViewAnimated.set(true);
  }

  private readonly communityAccess = inject(CommunityContentAccessService);
  private readonly lineCompanionPanel = inject(HdLineCompanionPanelService);

  /** Line Companion-iconen (community-content: feature + opt-in). */
  protected readonly lineCompanionVisible = computed(() => this.communityAccess.lineCompanionVisible());

  protected readonly tooltipLineCompanion = 'Line Companion (community-content)';

  protected openLineCompanion(event: MouseEvent): void {
    event.stopPropagation();
    this.lineCompanionPanel.openGate(this.context().gate, {
      origin: { x: event.clientX, y: event.clientY },
    });
  }

  protected openLineCompanionLine(lineNumber: number, event: MouseEvent): void {
    event.stopPropagation();
    this.lineCompanionPanel.openGate(this.context().gate, {
      focusLine: lineNumber,
      origin: { x: event.clientX, y: event.clientY },
    });
  }

  private readonly ichingAccess = inject(HdIChingAccessService);
  private readonly ichingPanel = inject(HdIChingPanelService);
  private readonly transloco = inject(TranslocoService);

  /** Classic I Ching-iconen (feature + setting). */
  protected readonly classicIChingVisible = this.ichingAccess.classicVisible;

  protected readonly classicIChingGateTooltip = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.transloco.translate(
      'humanDesign.chart.classicIChing.gateTooltip',
      { gate: this.context().gate },
      this.contentLang.effectiveLang(),
    );
  });

  /** Tooltip per lijnknop: `classicIChingLineTooltip()(lijn)`. */
  protected readonly classicIChingLineTooltip = computed(() => {
    this.contentLang.translationsLoadedTick();
    const gate = this.context().gate;
    const lang = this.contentLang.effectiveLang();
    return (line: number): string =>
      this.transloco.translate('humanDesign.chart.classicIChing.lineTooltip', { gate, line }, lang);
  });

  protected openClassicIChing(event: MouseEvent): void {
    event.stopPropagation();
    this.ichingPanel.openHexagram(this.context().gate, {
      origin: { x: event.clientX, y: event.clientY },
    });
  }

  protected openClassicIChingLine(lineNumber: number, event: MouseEvent): void {
    event.stopPropagation();
    this.ichingPanel.openHexagram(this.context().gate, {
      focusLine: lineNumber as IChingLinePosition,
      origin: { x: event.clientX, y: event.clientY },
    });
  }

  protected readonly effectiveHideGateNumber = computed(() => this.hideGateNumber() && !this.revealOverride());
  protected readonly effectiveHideKeynotes = computed(() => this.hideKeynotes() && !this.revealOverride());
  protected readonly effectiveHideColors = computed(() => this.hideColors() && !this.revealOverride());
  protected readonly effectiveHideFooterCircuit = computed(() => this.hideFooterCircuit() && !this.revealOverride());
  protected readonly effectiveHideFooterChannel = computed(() => this.hideFooterChannel() && !this.revealOverride());
  protected readonly effectiveHideBody = computed(() => this.hideBody() && !this.revealOverride());

  protected onCardClick(): void {
    if (!this.anyFlagActive()) {
      return;
    }
    this.cardClicked.emit(this.context().gate);
  }

  protected trackCircuit(_index: number, circuit: HdCheatSheetFooterCircuitViewModel): string {
    return circuit.key;
  }
}

/**
 * Compacte weergave van de kanaal-poort-paren voor de huidige poort.
 *
 * Bij één kanaal: canonieke key (bv. `64-47`).
 *
 * Bij meerdere kanalen: de huidige poort komt links, de partnerpoorten worden
 * rechts van het streepje samengevoegd met een schuine streep — bv. een poort
 * 10 met kanalen `10-20`, `10-34`, `10-57` rendert als `10-20/34/57`.
 * Dat scheelt drie keer het poortnummer en houdt de footer compact.
 */
function formatHdCheatSheetChannelKeys(channelKeys: readonly string[], currentGate: number): string {
  if (channelKeys.length === 0) {
    return '';
  }
  if (channelKeys.length === 1) {
    return channelKeys[0];
  }

  const partnerGates = channelKeys.map(key => {
    const [a, b] = key.split('-').map(Number);
    return a === currentGate ? b : a;
  });
  return `${currentGate}-${partnerGates.join('/')}`;
}

